/**
 * AI Studio conversation host page (foundation).
 *
 * Minimal authenticated host for the universal conversation shell. A supplied
 * runId binds the host to AGENT_RUN; otherwise it uses TMOS_OPERATIONS so every
 * persisted conversation has a valid governed context. It wires the shell to the
 * API: pick or create a conversation, load its separate history, then send through
 * the transport seam. Agent side panes, decision
 * cases, and context switching are deliberately out of scope for the foundation.
 *
 * URL parameters:
 *   ?conversationId=<uuid>  open an existing conversation
 *   ?runId=<uuid>           scope the list/creation to one agent run
 *   ?runName=<label>        optional human label for the AGENT_RUN scope chip
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import {
  ConversationPicker,
  ConversationShell,
  ConversationTitleEditor,
  readConversationTitle,
  useConversation,
} from '@/components/ai-studio/conversation';
import {
  ASSIST_PANE_HEADER_CLASS,
  ASSIST_PANE_SHELL_CLASS,
  ASSIST_WORKSPACE_CONTAINER_CLASS,
  ASSIST_WORKSPACE_PAGE_CLASS,
} from '@/components/ai-studio/assist/assistPaneStyles';
import {
  createConversation,
  getConversation,
  listConversationMessages,
  listConversations,
  renameConversation,
} from '@/services/aiStudioService';
import { toActiveContext } from '@/types/aiStudio';
import type {
  ActiveConversationContext,
  AiStudioConversation,
  AiStudioConversationSummary,
} from '@/types/aiStudio';

const AIStudioConversationPage: React.FC = () => {
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const conversationIdParam = searchParams.get('conversationId');
  const runIdParam = searchParams.get('runId');
  const runNameParam = searchParams.get('runName');
  const runScopeLabel = runNameParam?.trim() || runIdParam;
  const contextType = runIdParam ? ('AGENT_RUN' as const) : ('TMOS_OPERATIONS' as const);

  const [conversation, setConversation] = useState<AiStudioConversation | null>(null);
  const [summaries, setSummaries] = useState<AiStudioConversationSummary[]>([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [creating, setCreating] = useState(false);
  // Errors are stored as translation keys so data effects never depend on `t`,
  // whose identity changes on every language-provider render.
  const [pageErrorKey, setPageErrorKey] = useState<string | null>(null);

  const {
    messages,
    sending,
    error: sendError,
    setMessages,
    send,
  } = useConversation({
    conversationId: conversation?.id ?? null,
    sendErrorMessage: t('aiStudio.conversation.sendFailed'),
  });

  /** Load the picker list only while no conversation is selected. */
  useEffect(() => {
    if (conversationIdParam) return;
    let cancelled = false;
    setLoadingList(true);
    listConversations({ contextType, ...(runIdParam ? { runId: runIdParam } : {}) })
      .then((items) => {
        if (!cancelled) setSummaries(items);
      })
      .catch(() => {
        if (!cancelled) setPageErrorKey('aiStudio.conversation.listFailed');
      })
      .finally(() => {
        if (!cancelled) setLoadingList(false);
      });
    return () => {
      cancelled = true;
    };
  }, [contextType, conversationIdParam, runIdParam]);

  /** Load the selected conversation and replace the thread with its history. */
  useEffect(() => {
    if (!conversationIdParam) {
      setConversation(null);
      setMessages([]);
      return;
    }
    let cancelled = false;
    setLoadingConversation(true);
    setPageErrorKey(null);
    Promise.all([
      getConversation(conversationIdParam),
      listConversationMessages(conversationIdParam),
    ])
      .then(([loadedConversation, history]) => {
        if (cancelled) return;
        setConversation(loadedConversation);
        setMessages(history);
      })
      .catch(() => {
        if (!cancelled) setPageErrorKey('aiStudio.conversation.loadFailed');
      })
      .finally(() => {
        if (!cancelled) setLoadingConversation(false);
      });
    return () => {
      cancelled = true;
    };
  }, [conversationIdParam, setMessages]);

  const openConversation = useCallback(
    (id: string) => {
      const next = new URLSearchParams(searchParams);
      next.set('conversationId', id);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleCreate = useCallback(async () => {
    setCreating(true);
    setPageErrorKey(null);
    try {
      const res = await createConversation({
        contextType,
        ...(runIdParam ? { runId: runIdParam } : {}),
        contextRef: runIdParam
          ? { label: runScopeLabel ?? runIdParam }
          : {},
      });
      openConversation(res.id);
    } catch {
      setPageErrorKey('aiStudio.conversation.createFailed');
    } finally {
      setCreating(false);
    }
  }, [contextType, openConversation, runIdParam, runScopeLabel]);

  const handleRename = useCallback(
    async (title: string) => {
      if (!conversation) return;
      const updated = await renameConversation(conversation.id, title);
      setConversation(updated);
      setSummaries((current) =>
        current.map((row) =>
          row.id === updated.id
            ? {
                ...row,
                name: updated.name,
                shellMetadata: updated.shellMetadata,
                updatedAt: updated.updatedAt,
              }
            : row
        )
      );
    },
    [conversation]
  );

  const conversationTitle = conversation
    ? readConversationTitle(conversation.shellMetadata, conversation.name) ||
      t('aiStudio.conversation.untitled')
    : '';

  // Until a conversation exists, the indicator still shows the context the page is bound to.
  const activeContext: ActiveConversationContext = conversation
    ? toActiveContext(conversation)
    : { contextType, scopeLabel: runScopeLabel };

  return (
    <main className={`${ASSIST_WORKSPACE_PAGE_CLASS} w-full px-2 md:px-4`}>
      <div className={`${ASSIST_WORKSPACE_CONTAINER_CLASS} max-w-6xl`}>
        <header className="mb-2 flex-shrink-0 px-1">
          <h1 className="text-lg font-semibold text-[var(--color-surface-text)]">
            {t('aiStudio.conversation.title')}
          </h1>
          <p className="text-base text-[var(--color-surface-text-secondary)]">
            {t('aiStudio.conversation.subtitle')}
          </p>
        </header>

        {pageErrorKey && (
          <div
            role="alert"
            className="mb-2 flex-shrink-0 rounded border border-[var(--color-error)] bg-[var(--color-surface-bg)] px-4 py-2 text-base text-[var(--color-error)]"
          >
            {t(pageErrorKey)}
          </div>
        )}

        {!conversationIdParam ? (
          <div className={`${ASSIST_PANE_SHELL_CLASS} min-h-0 flex-1 p-4`}>
            <ConversationPicker
              conversations={summaries}
              loading={loadingList}
              creating={creating}
              onCreate={() => void handleCreate()}
              onSelect={openConversation}
            />
          </div>
        ) : loadingConversation && !conversation ? (
          <p className="text-base text-[var(--color-surface-text-secondary)]">
            {t('aiStudio.conversation.loading')}
          </p>
        ) : (
          <div className={`${ASSIST_PANE_SHELL_CLASS} min-h-0 flex-1`}>
            <header className={ASSIST_PANE_HEADER_CLASS}>
              <ConversationTitleEditor
                title={conversationTitle}
                onSave={handleRename}
                variant="inline"
                className="min-w-0 flex-1"
              />
            </header>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-[var(--color-secondary-bg)]">
              <ConversationShell
                className="min-h-0 flex-1"
                context={activeContext}
                messages={messages}
                sending={sending}
                error={sendError}
                onSend={(text) => void send(text)}
                disabled={!conversation}
                hideContextIndicator
                paneMode
                composerVariant="pill"
              />
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default AIStudioConversationPage;
